import { useRef, useEffect, useState, useCallback } from "react"
import { MessageSquare, X, Menu, Search, Bell, Plus, Baby, History } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ActivityCards } from "@/components/dashboard/ActivityCards"
import { AddChildDialog } from "@/components/AddChildDialog"
import { ModernParentSidebar } from "@/components/ModernParentSidebar"
import { useDashboard } from "@/hooks/use-dashboard"
import { cn } from "@/lib/utils"
import { DashboardLoading } from "@/components/ui/loading"
import { ErrorState } from "@/components/ui/error-state"
import { NoChildrenState } from "@/components/dashboard/empty-states"
import { useChatStore } from "@/lib/store/chat"
import { useAuthStore } from "@/lib/store/auth"
import { BUILD_TIMESTAMP } from "@/config/build-info"
import { IncomingCallNotification } from "@/components/video-call/IncomingCallNotification"
import { VideoCallRoom } from "@/components/video-call/VideoCallRoom"
import { useVideoCall } from "@/hooks/use-video-call"
import { StartVideoCallDialog } from "@/components/video-call/StartVideoCallDialog"
import { CallHistoryDialog } from "@/components/video-call/CallHistoryDialog"
import { SleepReminders } from "@/components/sleep/SleepReminders"
import { ScheduledCallsBanner } from "@/components/dashboard/ScheduledCallsBanner"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChildSelector } from "@/components/dashboard/child-selector/ChildSelector"
import { useTranslations } from '@/i18n/hooks'
import { SpecialistPanel } from "@/components/dashboard/SpecialistPanel"
import { AutoCreateTalkJSChat } from "@/components/chat/AutoCreateTalkJSChat"
import { useToast } from "@/components/ui/use-toast"
import { useChildStore } from "@/lib/store/child"

interface DashboardContentProps {
  className?: string
}

const DashboardContent = ({ className }: DashboardContentProps) => {
  console.log('[DASHBOARD COMPONENT] ===== RENDERING DASHBOARD =====', {
    timestamp: new Date().toISOString()
  })

  const {
    addChildOpen,
    setAddChildOpen,
    chatOpen,
    setChatOpen,
    children: fetchedChildren,
    isLoadingChildren,
    childrenError,
    handleAddChildSuccess,
  } = useDashboard()
  
  // Ensure children is always an array to prevent crashes
  const children = fetchedChildren || []

  const { toast } = useToast()
  const { user, role, session } = useAuthStore()
  const { activeChat, clearActiveChat } = useChatStore()
  const t = useTranslations('dashboard');
  const { selectedChild } = useChildStore();
  
  // Get incomplete sessions count for notifications
  const { data: incompleteSessions = [] } = useQuery<{ id: string }[]>({
    queryKey: ['incompleteSessions', selectedChild?.id],
    queryFn: async () => {
      if (!selectedChild?.id) return [];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (supabase as any)
        .from('sleep_sessions')
        .select('id')
        .eq('child_id', selectedChild.id)
        .eq('status', 'in_progress')
        .limit(10);

      if (result.error) throw result.error;
      return result.data || [];
    },
    enabled: !!selectedChild?.id,
    refetchInterval: 60000,
  });

  const reminderCount = incompleteSessions.length;
  
  // Video call state for caregivers - pass user.id as the userId and false for isSpecialist
  const {
    incomingCall,
    token: videoToken,
    wsUrl: videoWsUrl,
    roomName: videoRoomName,
    status: videoCallStatus,
    joinCall,
    declineCall,
    endCall,
    setConnected,
  } = useVideoCall(user?.id, false);
  
  // State to track if we're in an active video call
  const [isInVideoCall, setIsInVideoCall] = useState(false);
  const [caregiverId, setCaregiverId] = useState<string | null>(null);
  const [videoCallDialogOpen, setVideoCallDialogOpen] = useState(false);
  const [videoCallTarget, setVideoCallTarget] = useState<{ specialistId: string; specialistName: string } | null>(null);
  
  // Fetch caregiver ID on mount
  useEffect(() => {
    const fetchCaregiverId = async () => {
      if (user?.id) {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data } = await supabase
          .from('caregivers')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();
        if (data) {
          setCaregiverId(data.id);
          console.log('[VIDEO CALL] Caregiver ID fetched:', data.id);
        }
      }
    };
    fetchCaregiverId();
  }, [user?.id]);
  
  // Log incoming call for debugging
  useEffect(() => {
    if (incomingCall) {
      console.log('[VIDEO CALL] Incoming call received:', incomingCall);
    }
  }, [incomingCall]);
  
  // Handle accepting incoming call
  const handleAcceptCall = async () => {
    if (incomingCall && user && caregiverId) {
      try {
        console.log('[VIDEO CALL] Accepting call:', { callId: incomingCall.id, roomName: incomingCall.roomName, caregiverId });
        const caregiverName = user.user_metadata?.name || user.email || 'Caregiver';
        await joinCall(incomingCall.id, incomingCall.roomName, caregiverId, caregiverName);
        setIsInVideoCall(true);
      } catch (error) {
        console.error('[VIDEO CALL] Failed to accept call:', error);
        toast({
          variant: "destructive",
          title: "Call Error",
          description: "Failed to join the video call. Please try again.",
        });
      }
    } else {
      console.error('[VIDEO CALL] Cannot accept call - missing data:', { incomingCall, user: !!user, caregiverId });
    }
  };
  
  // Handle declining incoming call
  const handleDeclineCall = () => {
    console.log('[VIDEO CALL] Declining call');
    declineCall();
  };
  
  // Handle disconnecting from video call
  const handleDisconnectCall = () => {
    console.log('[VIDEO CALL] Disconnecting from call');
    endCall();
    setIsInVideoCall(false);
  };
  
  // Handle starting a video call to specialist
  const handleVideoCallClick = (specialistId: string, specialistName: string) => {
    setVideoCallTarget({ specialistId, specialistName });
    setVideoCallDialogOpen(true);
  };
  
  // Handle video call started
  const handleVideoCallStarted = () => {
    setIsInVideoCall(true);
    setVideoCallDialogOpen(false);
  };

  console.log('[DASHBOARD COMPONENT] Dashboard state', {
    isLoadingChildren,
    hasError: !!childrenError,
    childrenCount: children?.length || 0,
    userId: user?.id,
    userRole: role,
    sessionRole: session?.user?.user_metadata?.role,
    sessionRoles: session?.user?.user_metadata?.roles
  })

  // Error tracking state
  const [hasError, setHasError] = useState(false)
  
  // Error boundary fallback
  useEffect(() => {
    if (childrenError && !hasError) {
      console.error('[DASHBOARD COMPONENT] !!! ERROR DETECTED !!!', {
        error: childrenError,
        userId: user?.id,
        userRole: role
      })
      setHasError(true)
      toast({
        variant: "destructive",
        title: "Dashboard Error",
        description: "There was an error loading your dashboard. Please try refreshing the page.",
      })
    }
  }, [childrenError, hasError, toast, user?.id, role])

  // Handle chat close event
  const handleCloseChat = () => {
    clearActiveChat();
    setChatOpen(false);
    
    // Force TalkJS cleanup by adding a small timeout
    // This ensures that any ongoing operations complete before reinitializing
    setTimeout(() => {
      // Additional cleanup can happen here if needed
      // The timeout helps ensure TalkJS has fully cleaned up
    }, 100);
  };

  if (isLoadingChildren) {
    console.log('[DASHBOARD COMPONENT] Showing loading state')
    return <DashboardLoading />
  }

  if (childrenError || hasError) {
    const errorMessage = typeof childrenError === 'string'
      ? childrenError
      : childrenError?.message;

    console.error('[DASHBOARD COMPONENT] Showing error state', {
      childrenError,
      hasError,
      userId: user?.id,
      userRole: role,
    })
    return (
      <ErrorState 
        title="Error Loading Caregiver Dashboard"
        message={errorMessage || "There was an issue loading your caregiver dashboard. Please try refreshing the page."}
      />
    )
  }

  console.log('[DASHBOARD COMPONENT] Rendering main dashboard content')

  return (
    <div className={cn(
      className, 
      "flex flex-col theme-bg-primary min-h-screen w-full max-w-full",
      "bg-gradient-to-br dark:from-[#0F172A] dark:to-[#1E293B] light:from-slate-50 light:to-white"
    )}>
      <div className="flex flex-1 overflow-hidden w-full max-w-full">
        {/* Modern Sidebar */}
        <ModernParentSidebar />

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden w-full max-w-full">
          {children.length === 0 ? (
            <NoChildrenState onAddChild={() => setAddChildOpen(true)} />
          ) : (
            <>
              {/* Top Bar with Welcome and Controls */}
              <div className="border-b dark:border-white/15 light:border-slate-200 dark:bg-white/5 light:bg-white backdrop-blur-xl px-3 sm:px-6 lg:px-8 py-3 sm:py-5 w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div>
                      <h2 className="text-base sm:text-xl lg:text-2xl font-bold tracking-tight dark:text-slate-200 light:text-slate-800">
                        {t('welcome', { name: user?.user_metadata?.name || 'Parent' })}
                      </h2>
                      <p className="text-2xs sm:text-sm dark:text-slate-400 light:text-slate-600 mt-0.5">
                        Track and manage your child's daily activities
                      </p>
                    </div>
                  </div>
                  
                </div>

                {/* Child Selector */}
                <div className="mt-3 sm:mt-4 lg:mt-6">
                  <ChildSelector
                    connectToStore={true}
                    onAddChild={() => setAddChildOpen(true)}
                  />
                </div>
                </div>

              {/* Main Dashboard Grid */}
              <div className="flex-1 px-3 sm:p-6 lg:p-8 py-4 space-y-4 sm:space-y-6 overflow-y-auto w-full max-w-full pb-16">
                
                {/* Sleep Reminders Notification Banner */}
                {reminderCount > 0 && selectedChild && (
                  <div className="w-full animate-fade-in">
                    <Card className="border-primary/30 bg-primary/5 dark:bg-primary/10">
                      <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-6">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="relative">
                            <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-2xs sm:text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center font-bold">
                              {reminderCount}
                            </span>
                          </div>
                          <div>
                            <CardTitle className="text-base sm:text-lg text-primary">Sleep Reminders</CardTitle>
                            <CardDescription className="text-2xs sm:text-sm">
                              You have {reminderCount} incomplete sleep session{reminderCount > 1 ? 's' : ''} to complete
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                        <SleepReminders 
                          childId={selectedChild.id}
                          childName={selectedChild.name}
                        />
                      </CardContent>
                    </Card>
                  </div>
                )}
                
                {/* Scheduled AI Calls Banner */}
                {caregiverId && (
                  <ScheduledCallsBanner caregiverId={caregiverId} />
                )}
                
                {/* Primary Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 w-full max-w-full">
                  {/* Activity Cards Section - full width on mobile, 2 columns on desktop */}
                  <div className="lg:col-span-2 w-full">
                    <div className="track-activities-container animate-fade-in">
                      <div className="track-activities-header mb-3">
                        <h2 className="text-base sm:text-lg font-semibold">Track Activities</h2>
                        <p className="text-2xs sm:text-sm dark:text-slate-400 light:text-slate-600">Log your child's daily activities</p>
                      </div>
                      <div className="track-activities-content">
                        <ActivityCards />
                      </div>
                    </div>
                  </div>

                  {/* Sleep Specialist Panel - full width on mobile, 1 column on desktop */}
                  <div className="lg:col-span-1 w-full">
                    <SpecialistPanel
                      onChatClick={() => setChatOpen(true)}
                      onVideoCallClick={handleVideoCallClick}
                    />
                  </div>
                </div>
              </div>
              
              {/* Footer with publish date - inside main content flow */}
              <footer className="border-t border-border/40 bg-muted/20 py-2 sm:py-3 px-3 sm:px-6 mt-auto">
                <div className="text-center text-2xs sm:text-xs text-muted-foreground">
                  App published: {new Date(BUILD_TIMESTAMP).toLocaleString()}
                </div>
              </footer>
            </>
          )}
        </main>
      </div>

      {/* Add Child Dialog */}
      <AddChildDialog
        open={addChildOpen}
        onOpenChange={setAddChildOpen}
        onSuccess={handleAddChildSuccess}
      />

      {/* Chat Implementation - more mobile friendly */}
      {chatOpen && activeChat && user && (
        <div className="fixed bottom-0 right-0 w-full sm:bottom-4 sm:right-4 sm:w-[400px] h-[80vh] sm:h-[600px] max-h-[80vh] sm:max-h-[600px] dark:bg-[#1C1C1C] light:bg-white rounded-t-lg sm:rounded-lg shadow-2xl dark:border-[#2A2A2A] light:border-slate-200 overflow-hidden z-50">
          <div className="flex flex-col h-full">
            <div className="dark:bg-[#2A2A2A] light:bg-slate-100 p-3 flex justify-between items-center">
              <div className="dark:text-white light:text-slate-800 font-medium truncate">{activeChat.specialistName || 'Specialist'}</div>
              <button 
                onClick={handleCloseChat}
                className="dark:text-gray-300 light:text-slate-600 dark:hover:text-white light:hover:text-slate-900"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <AutoCreateTalkJSChat
                key={`chat-${activeChat.specialistId}-${activeChat.roomId}-${Date.now()}`}
                appId={import.meta.env.VITE_TALKJS_APP_ID || '0r3onVUF'}
                userId={user.id}
                conversationid={activeChat.roomId}
                otherUserId={activeChat.specialistId}
                userDisplayName={user.user_metadata?.name || user.email || 'Caregiver'}
                otherUserDisplayName={activeChat.specialistName || 'Specialist'}
                isSpecialist={false}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Incoming Video Call Notification */}
      {incomingCall && !isInVideoCall && (
        <IncomingCallNotification
          callerName={incomingCall.callerName}
          callerRole={incomingCall.initiatedBy}
          onAccept={handleAcceptCall}
          onDecline={handleDeclineCall}
          isBotCall={incomingCall.isBotCall}
        />
      )}
      
      {/* Start Video Call Dialog (caregiver calling specialist) */}
      {videoCallTarget && caregiverId && (
        <StartVideoCallDialog
          open={videoCallDialogOpen}
          onOpenChange={setVideoCallDialogOpen}
          specialistId={videoCallTarget.specialistId}
          specialistName={videoCallTarget.specialistName}
          caregiverId={caregiverId}
          caregiverName={user?.user_metadata?.name || user?.email || 'Caregiver'}
          onCallStarted={handleVideoCallStarted}
          initiatedBy="caregiver"
        />
      )}
      
      {/* Active Video Call Room */}
      {isInVideoCall && videoToken && videoWsUrl && videoRoomName && (
        <VideoCallRoom
          token={videoToken}
          wsUrl={videoWsUrl}
          roomName={videoRoomName}
          onDisconnect={handleDisconnectCall}
          onConnected={setConnected}
        />
      )}
    </div>
  )
}

export function Dashboard() {
  return <DashboardContent />
}

export default Dashboard
